import { ExternalLink, BookOpen, Phone, AlertTriangle } from "lucide-react";
import { NDIS_RESOURCES } from "@/lib/constants";

const CRISIS = [
  { title: "Lifeline", number: "13 11 14", desc: "24/7 crisis support and suicide prevention" },
  { title: "1800RESPECT", number: "1800 737 732", desc: "24/7 sexual assault, domestic & family violence counselling" },
  { title: "Beyond Blue", number: "1300 22 4636", desc: "Mental health support 24/7" },
  { title: "Open Arms (Veterans)", number: "1800 011 046", desc: "24/7 counselling for veterans & families" },
  { title: "13YARN", number: "13 92 76", desc: "Crisis support line for Aboriginal & Torres Strait Islander peoples" },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-5 fade-up">Important Resources & Community Contacts</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            Trusted links, <span className="italic" style={{ color: "var(--navica-coral)" }}>when you need them</span>.
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl fade-up fade-up-2">
            A curated set of resources we use and recommend. From NDIS information to crisis support and accredited directories.
          </p>
        </div>
      </section>

      {/* CRISIS BANNER */}
      <section className="pb-12" data-testid="crisis-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="bg-[#284952] text-white rounded-sm p-8 lg:p-12 relative overflow-hidden">
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <AlertTriangle className="w-6 h-6 text-[#EA823A]" />
                <div className="text-xs uppercase tracking-[0.2em] text-[#F4C3CC] font-semibold">In crisis? Call now.</div>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl mb-7 leading-tight max-w-2xl">
                If you or someone you know is in immediate danger, call <span className="italic text-[#EA823A]">000</span>.
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CRISIS.map((c) => (
                  <a key={c.title} href={`tel:${c.number.replace(/\s+/g, "")}`} className="bg-white/5 hover:bg-white/10 rounded-2xl p-5 transition" data-testid={`crisis-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Phone className="w-4 h-4 text-[#EA823A]" />
                      <span className="font-display text-xl text-white">{c.title}</span>
                    </div>
                    <div className="font-mono text-lg text-[#F4C3CC] mb-1">{c.number}</div>
                    <div className="text-xs text-white/70">{c.desc}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESOURCE LINKS */}
      <section className="pb-24" data-testid="resources-list">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <BookOpen className="w-6 h-6 text-[#E2718A]" />
            <h2 className="font-display text-3xl text-[#1F2A2E]">Directories & information</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {NDIS_RESOURCES.map((r, idx) => (
              <a
                key={r.url}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card-soft group flex items-start gap-5 fade-up"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`resource-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#F1E4D5] flex items-center justify-center shrink-0 group-hover:bg-[#FCE7EB] transition">
                  <ExternalLink className="w-5 h-5 text-[#E2718A]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg text-[#1F2A2E] mb-2 group-hover:text-[#E2718A] transition">{r.title}</h3>
                  <p className="text-sm text-[#4A595E] leading-relaxed">{r.desc}</p>
                  <div className="text-xs text-[#6B7A80] mt-3 truncate">{r.url.replace(/^https?:\/\//, "")}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
